import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Select,
  Stack,
  Text,
  Tooltip,
} from '@chakra-ui/react'
import type { InsuranceParamsDto, OfferAddon } from 'BrokerTsLib/src/dtos'
import { RiskVariant } from 'BrokerTsLib/src/enums'
import { useForm } from 'BrokerTsLib/src/rhf'
import { useContext, useEffect } from 'react'
import { carkulkaExtraOffersContext } from '../../../../context/CarkulkaExtraOffersProvider'
import { useGetAddonProductEnums, useGetAddonRiskVariantProducts } from '../../../../hooks'
import { useChangeParams, useChangePrice, useSelectVariant } from '../../../../hooks/useOfferAddon'
import { LabelHintIcon, Refresh } from '../../../../icons'
import { insuranceParamsContext } from '../../../../layouts/Wizard'
import { EditableTotal } from '../../../EditableTotal'


interface BasicInfoExtraTabProps {
  offers: OfferAddon[]
}

export const BasicInfoExtraTab = ({ offers }: BasicInfoExtraTabProps) => {
  const { insuranceParams } = useContext(insuranceParamsContext)
  const insuranceParamsDto = insuranceParams as InsuranceParamsDto
  const { recalculateOffers: recalculateAddonOffers, refetchOffers: refetchAddonOffers, updateOffer: updateAddonOffer } = useContext(carkulkaExtraOffersContext)
  const { data: addonRiskVariantProducts, mutateAsync: getAddonRiskVariantProducts } = useGetAddonRiskVariantProducts()
  const { data: addonProductEnums, mutateAsync: getAddonProductEnums } = useGetAddonProductEnums()
  const { mutateAsync: changePrice } = useChangePrice()
  const { mutateAsync: selectVariant } = useSelectVariant()
  const { mutateAsync: changeParams } = useChangeParams()
  const isContracted = !!insuranceParams?.generalParams.contractNumber

  const defendEditPriceTooltip = 'Nastavení ceny má vliv na provizi, při zadání nejnižší možné částky je provize 0%'

  const activeOffer = offers.find((x) => x.isActive)!
  const isAllriskPlusGap = (insuranceParamsDto?.baseParams.hav?.findIndex((x) => x == RiskVariant.AllriskPlusGAP,) ?? -1) >= 0 && activeOffer?.riskVariantId == RiskVariant.AddonGAP

  const hasError = (activeOffer?.errors?.length ?? 0) > 0

  useEffect(() => {
    getAddonRiskVariantProducts({
      offerId: activeOffer?.offerId,
      riskVariantId: activeOffer?.riskVariantId,
    })

    getAddonProductEnums({
      offerId: activeOffer?.offerId,
      riskVariantId: activeOffer?.riskVariantId,
      changedParams: [],
      filterOutput: true
    })
  }, [])

  // konverze z pole na object
  const defaultValues = activeOffer.params?.parameterValues.reduce((obj: any, c) => {
    return { ...obj, [c.name]: c.value }
  }, {})

  const { getValues, setValue, watch, handleSubmit, reset } = useForm({ defaultValues: defaultValues })

  useEffect(() => {
    // při změně offer je třeba resetno formulář, aby se nepostovaly hodnoty parametrů předchozí offery
    reset(defaultValues)
  }, [activeOffer.offerId])

  const allWithSameVariant = offers.filter((x) =>
    x.productId == activeOffer.productId && x.price > 0)

  const getParamsDataArr = (values: any) =>
    Object.keys(values).map((key) => {
      return { name: key, value: values[key] }
    })
  const changedParams =
    getParamsDataArr(getValues()).filter(
      (x) =>
        (activeOffer.params?.parameterValues.findIndex(
          (y) => y.name == x.name && y.value == x.value,
        ) ?? -1) < 0,
    ) ?? []

  const handleChangeVariant = (productId: string) => {
    selectVariant({ activeOfferId: activeOffer.offerId, productId: parseInt(productId) })
      .then(
        (data) =>
          getAddonProductEnums({
            offerId: data.data,
            riskVariantId: activeOffer?.riskVariantId,
            changedParams: [],
            filterOutput: true
          }).then(() => refetchAddonOffers())
      )
  }

  const handleChangeParams = (paramName: string, paramValue: string) => {
    setValue(paramName, paramValue)

    getAddonProductEnums({
      offerId: activeOffer?.offerId,
      riskVariantId: activeOffer?.riskVariantId,
      changedParams: changedParams,
      filterOutput: true
    })
  }

  const submit = handleSubmit((values) => {
    // ulozim zmenu parametru
    changeParams({
      activeOfferId: activeOffer.offerId,
      parameterValues: getParamsDataArr(values),
    }).then(() => {
      // vynutim rekalkulaci aktivni offery.
      // update v addonOffers se provede automaticky pres signalR
      recalculateAddonOffers([activeOffer.offerId], true)
    })
  })

  return (
    <Box
      sx={{
        display: { base: 'grid' },
        gridTemplateColumns: { base: '1fr', md: '2fr 1fr' },
        columnGap: 8,
        rowGap: 4,
      }}
    >
      <Box sx={{ display: 'grid', gap: 4, alignContent: 'start' }}>
        <Flex sx={{ columnGap: 12, rowGap: 4, flexWrap: 'wrap' }}>
          {addonRiskVariantProducts?.data &&
            addonRiskVariantProducts.data.length == 0 &&
            addonProductEnums?.data &&
            addonProductEnums.data.length == 0 && (
            <FormLabel><Text dangerouslySetInnerHTML={{ __html: activeOffer.productDescription ?? '' }}></Text></FormLabel>
          )}

          {addonRiskVariantProducts?.data && addonRiskVariantProducts.data.length > 0 && (
            <FormControl>
              <FormLabel>Varianta</FormLabel>
              <Stack direction="row" sx={{ alignItems: 'center' }}>
                <Select
                  id="variant"
                  value={activeOffer.productId.toString()}
                  disabled={addonRiskVariantProducts?.data.length == 1 || isContracted}
                  onChange={(e) => {
                    handleChangeVariant(e.target.value)
                  }}
                >
                  {addonRiskVariantProducts?.data.map((x) => (
                    <option key={x.id} value={x.id.toString()}>
                      {x.name}
                    </option>
                  ))}
                </Select>

                {activeOffer.productDescription && (
                  <Tooltip
                    hasArrow
                    placement="top"
                    label={
                      <Box dangerouslySetInnerHTML={{ __html: activeOffer.productDescription }}></Box>
                    }
                  >
                    <Box sx={{ lineHeight: 0 }}>
                      <LabelHintIcon width={4} height={4} />
                    </Box>
                  </Tooltip>
                )}
              </Stack>
            </FormControl>
          )}

          {addonProductEnums?.data.map((x, i) => (
            <FormControl key={x.paramName}>
              <FormLabel>{x.paramDescription}</FormLabel>

              <Select
                id={x.paramName}
                value={watch(x.paramName)?.toString()}
                onChange={(e) => handleChangeParams(x.paramName, e.target.value)}
                disabled={x.values.length == 1 || isContracted}
              >
                {x.values?.map((product) => (
                  <option key={product.value} value={product.value.toString()}>
                    {product.text}
                  </option>
                ))}
              </Select>
            </FormControl>
          ))}
        </Flex>
      </Box>

      {/* Total */}
      {typeof activeOffer.price !== 'undefined' && (
        <Stack direction="column">
          <EditableTotal
            label={allWithSameVariant.length == 1 ? 'Pojistné za rok' : 'Pojistné za rok již od'}
            price={
              allWithSameVariant.sort((x, y) => x.pricePerYear! - y.pricePerYear!)?.at(0)?.pricePerYear
            }
            size="lg"
            color="gray.50"
            tooltip={defendEditPriceTooltip}
            onChange={(value: number, successAction: () => void, errorAction: (error: string) => void) => {
              changePrice({ offerId: activeOffer.offerId, price: value }).then(
                ({ data: updatedOffer }) => {
                  successAction()
                  updateAddonOffer(updatedOffer)
                },
              ).catch((reason) => {
                let error = 'Nelze uložit změnu ceny'
                if (reason.response.status == 400) {
                  error = reason.response.data
                }
                errorAction(error)
              })
            }}
            priceMin={activeOffer.priceMin}
            priceMax={activeOffer.priceMax}
            isEditable={
              !isContracted &&
              !hasError &&
              !isAllriskPlusGap &&
              allWithSameVariant.length == 1 &&
              (!!activeOffer.priceMax || !!activeOffer.priceMin)
            } // editovatelné jen pokud jsou nastavené min/max hodnoty
          />
          {changedParams.length > 0 && (
            <Flex justifyContent="end">
              <Button
                variant="outline"
                isDisabled={false}
                isLoading={false}
                loadingText="Přepočítávám..."
                onClick={() => submit()}
                gridColumnStart={1}
              >
                <Refresh color="secondary.500" />
                Přepočítat
              </Button>
            </Flex>
          )}
        </Stack>
      )}
    </Box>
  )
}